import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { MapPin } from "lucide-react";
import { fetchGeoStats } from "@/lib/api";

/**
 * GeoHeatmap — DACH map showing QR-scan distribution across the 18 SEO cities.
 *
 * Visualisation: 2D scatter of city lng/lat with circle size proportional
 * to scan count. The cluster of dots naturally forms a recognizable DACH
 * outline (Berlin top-right, Munich south, Zurich south-west, Vienna east).
 */
const COLOR_HIGH = "#1F2420";
const COLOR_MID = "#6F7F61";
const COLOR_LOW = "#C8D4BC";
const COLOR_ZERO = "#E0E2DC";

function tierColor(count, max) {
  if (!count) return COLOR_ZERO;
  const ratio = count / Math.max(max, 1);
  if (ratio > 0.6) return COLOR_HIGH;
  if (ratio > 0.25) return COLOR_MID;
  return COLOR_LOW;
}

function CityTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, scans } = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #D4D7D0",
        padding: "8px 12px",
        fontSize: 12,
        color: "#1F2420",
      }}
    >
      <div className="font-medium">{name}</div>
      <div className="text-muted">{scans} {scans === 1 ? "Scan" : "Scans"}</div>
    </div>
  );
}

export default function GeoHeatmap() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["geo-stats"],
    queryFn: () => fetchGeoStats(30),
    refetchInterval: 60_000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="card-standard" data-testid="geo-heatmap-loading">
        <div className="form-label" style={{ color: "var(--color-accent)" }}>
          Geo-Heatmap
        </div>
        <div className="body-md mt-4">Lade Geo-Daten…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-standard" data-testid="geo-heatmap-error">
        <div className="form-label" style={{ color: "var(--color-accent)" }}>
          Geo-Heatmap
        </div>
        <div className="body-md mt-4 text-muted">Fehler beim Laden der Geo-Daten.</div>
      </div>
    );
  }

  const cities = data?.cities || [];
  const totalScans = data?.total_city_scans || 0;
  const leadsByCity = data?.leads_by_city || [];
  const maxScans = Math.max(...cities.map((c) => c.scans), 1);
  const cityData = cities.map((c) => ({
    name: c.name,
    slug: c.slug,
    lng: c.lng,
    lat: c.lat,
    scans: c.scans,
  }));

  return (
    <div className="card-standard" data-testid="geo-heatmap" style={{ padding: 24 }}>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="form-label" style={{ color: "var(--color-accent)" }}>
            <MapPin size={12} className="inline -mt-0.5 mr-1" />
            Geo-Heatmap · DACH
          </div>
          <div className="body-md mt-1">
            Stadt-spezifische Landingpage-Aufrufe der letzten 30 Tage
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted uppercase tracking-widest">
          <span data-testid="geo-total-scans">
            <strong className="text-primary not-italic">{totalScans}</strong> Scans gesamt
          </span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">
            <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" style={{ background: COLOR_HIGH }}></span>
            Hoch
            <span className="inline-block w-2.5 h-2.5 rounded-full ml-3 mr-1.5 align-middle" style={{ background: COLOR_MID }}></span>
            Mittel
            <span className="inline-block w-2.5 h-2.5 rounded-full ml-3 mr-1.5 align-middle" style={{ background: COLOR_LOW }}></span>
            Niedrig
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0 h-[420px] relative" data-testid="geo-heatmap-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 16, right: 16, bottom: 28, left: 8 }}>
              <CartesianGrid stroke="#E0E2DC" strokeDasharray="2 2" />
              <XAxis
                type="number"
                dataKey="lng"
                domain={[5.5, 17.5]}
                tick={{ fontSize: 10, fill: "#8A8F87" }}
                stroke="#8A8F87"
              />
              <YAxis
                type="number"
                dataKey="lat"
                domain={[46.5, 55.0]}
                tick={{ fontSize: 10, fill: "#8A8F87" }}
                stroke="#8A8F87"
                width={32}
              />
              <ZAxis type="number" dataKey="scans" range={[60, 600]} />
              <Tooltip content={<CityTooltip />} cursor={false} />
              <Scatter name="Cities" data={cityData}>
                {cityData.map((entry) => (
                  <Cell key={entry.slug} fill={tierColor(entry.scans, maxScans)} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-1 min-w-0" data-testid="geo-city-ranking">
          <div className="form-label mb-3" style={{ color: "var(--color-accent)" }}>
            Top Städte
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
            {cities.slice(0, 18).map((c, i) => {
              const ratio = maxScans > 0 ? (c.scans / maxScans) * 100 : 0;
              return (
                <div
                  key={c.slug}
                  data-testid={`geo-city-row-${i}`}
                  className="flex items-center gap-3"
                >
                  <span className="text-xs text-muted w-5 tabular-nums">{i + 1}</span>
                  <span className="text-sm text-primary flex-1 truncate">{c.name}</span>
                  <span className="text-sm tabular-nums text-muted w-10 text-right">{c.scans}</span>
                  <div className="w-16 h-1.5 bg-surface rounded-full overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${ratio}%`,
                        background: tierColor(c.scans, maxScans),
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {leadsByCity.length > 0 && (
        <div className="mt-8 pt-6 border-t border-soft" data-testid="leads-by-city">
          <div className="form-label mb-3" style={{ color: "var(--color-accent)" }}>
            Partner-Leads nach Stadt
          </div>
          <div className="flex flex-wrap gap-2">
            {leadsByCity.map((entry, i) => (
              <span
                key={i}
                className="inline-flex items-center px-3 py-1 border border-soft rounded-full text-xs text-primary"
                data-testid={`lead-city-chip-${i}`}
              >
                {entry.city}
                <span className="ml-2 text-muted">{entry.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
